import React, { useEffect, useRef, useState, useContext } from 'react';
import Chart from 'chart.js/auto';
import { ApiContext } from '../../../../../context/MyContext';
import * as userService from '../../../../../service/userService';
import { parseLocalDate } from '../../../../../utils/formatLocalDate';
const WeightGraphModal = ({ profileId, selectedDate, onClose }) => {
  const chartRef = useRef(null);
  const { get } = useContext(ApiContext);

  const [range, setRange] = useState('D');
  const [weightData, setWeightData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [trend, setTrend] = useState('');
  const [currentWeight, setCurrentWeight] = useState(0);
  const [targetWeight, setTargetWeight] = useState(0);
  const [startWeight, setStartWeight] = useState(0);
  const [averageValue, setAverageValue] = useState(null);
  const [weightChange, setWeightChange] = useState(null);


  const fetchChartData = async () => {
    const result = await userService.fetchWeightSummary(get, profileId, range, selectedDate);
    
    if (result.success) {
      const data = result.data || [];
      setTrend(result.trend || '');
      setAverageValue(result.average_value || 0);
      setWeightChange(result.weight_change || 0);
  
      if (data.length === 0) {
        setWeightData([]);
        setLabels([]);
        setStartWeight(0);
        setCurrentWeight(0);
        setTargetWeight(0);
        return;
      }
  
      setWeightData(data.map(d => d.value));
      setLabels(data.map(d => {
        const date = parseLocalDate(d.date);
        return range === 'D'
          ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
          : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }));
      setStartWeight(data[0].value);
      setCurrentWeight(data[data.length - 1].value);
      setTargetWeight(data[data.length - 1].recommended);
    }
  };
  

  useEffect(() => {
    fetchChartData();
  }, [range]);

  useEffect(() => {
    if (!chartRef.current || !weightData.length) return;

    const min = Math.min(...weightData, targetWeight);
    const max = Math.max(...weightData, targetWeight);

    const chart = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data: weightData,
          borderColor: '#FF6414',
          borderWidth: 3,
          tension: 0.3,
          pointRadius: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            min: Math.floor(min - 2),
            max: Math.ceil(max + 2),
            ticks: {
              callback: val => `${val} Kg`
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => `Weight: ${ctx.raw} Kg`
            }
          }
        }
      },
      plugins: [{
        id: 'annotations',
        afterDraw(chart) {
          const { ctx, chartArea: { left, right: _right }, scales } = chart;
          ctx.save();
          ctx.fillStyle = '#FF6414';
          ctx.font = '12px sans-serif';
          const goalY = scales.y.getPixelForValue(targetWeight);
          const startY = scales.y.getPixelForValue(startWeight);
          const currentY = scales.y.getPixelForValue(currentWeight);
          ctx.fillText(`Start: ${startWeight} Kgs`, left + 5, startY - 6);
          ctx.fillText(`Goal: ${targetWeight} Kgs`, left + 5, goalY - 6);
          ctx.fillText(`Current: ${currentWeight} Kgs`, left + 5, currentY - 6);
          ctx.restore();
        }
      }]
    });

    return () => chart.destroy();
  }, [weightData]);

  return (
    <div className="fixed inset-0 bg-orange-primary bg-opacity-20 z-50 flex justify-center items-center">
      <div className="bg-white w-[90%] max-w-3xl rounded-lg p-4 relative">
        <button onClick={onClose} className="absolute top-2 right-4 text-gray-700">✕</button>
        <h2 className="text-xl font-bold mb-2 text-center">Weight Analytics</h2>
        <div className="flex justify-center gap-4 mb-2">
          {['D', 'W'].map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-full border ${
                r === range ? 'bg-orange-primary text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        {weightData.length > 0 ? (
        <div className="w-full h-[300px]">
            <canvas ref={chartRef}></canvas>
        </div>
        ) : (
        <div className="w-full h-[300px] flex items-center justify-center text-gray-500 font-medium">
            No weight data available for this range
        </div>
        )}

        <div className="mt-4 text-center font-medium space-y-1">
        <div>
            <span className="text-gray-700">Average Weight: </span>
            <span className="text-black">{averageValue?.toFixed(1)} Kg</span>
        </div>
        <div>
            <span className="text-gray-700">Weight Change: </span>
            <span className="text-black">{weightChange > 0 ? '+' : ''}{weightChange} Kg</span>
        </div>
        <div>
            <span className="text-gray-700">Trend: </span>
            <span className="text-black">{trend}</span>
        </div>
        </div>

      </div>
    </div>
  );
};

export default WeightGraphModal;
